import {DEFAULT_BODY} from "./constants";
import type {CreateAutomationFormValues} from "./schema";
import type {EmailAutomationRow, EmailAutomationApiItem} from "./types";

/** Matches `OfferingOption` from offerings hook — form multiselect values are `category:offeringId`. */
export type OfferingSelectOption = {
  value: string;
  offeringId: number;
};

/** Map API pivot ids to Select option values so Ant Design shows labels, not bare ids. */
export function resolveOfferingIdsForMultiselect(
  offeringIds: number[],
  options: OfferingSelectOption[]
): string[] {
  if (!options.length) {
    return offeringIds.map(String);
  }
  return offeringIds.map(
    (id) => options.find((o) => o.offeringId === id)?.value ?? String(id)
  );
}

/** Mock form values per automation for edit prefilling (keyed by row key). */
const EDIT_FORM_VALUES_BY_KEY: Record<string, Partial<CreateAutomationFormValues>> = {
  "1": {
    automationName: "Course purchase email",
    subject: "Thanks for purchasing {Offering_name}, {Customer_name}",
    body: DEFAULT_BODY,
    addCustomButton: true,
    buttonText: "Access course",
    buttonLink: "https://example.com/access",
    trigger: "after_purchase",
    offerings: ["offering-1"],
    sendTiming: "custom_delay",
    delayValue: 2,
    delayUnit: "days",
  },
  "2": {
    automationName: "Lead magnet delivery",
    subject: "Your lead magnet: {Offering_name}",
    body: `<p>Hi {Customer_name},</p><p>Here is your lead magnet. Enjoy!</p><p>Best,</p><p>{Coach_name}</p>`,
    addCustomButton: true,
    buttonText: "Download",
    buttonLink: "https://example.com/download",
    trigger: "after_purchase",
    offerings: ["offering-2", "offering-3"],
    sendTiming: "immediately",
    delayValue: 5,
    delayUnit: "minutes",
  },
  "3": {
    automationName: "Welcome sequence",
    subject: "Welcome, {Customer_name}!",
    body: DEFAULT_BODY,
    addCustomButton: false,
    buttonText: "",
    buttonLink: "",
    trigger: "after_purchase",
    offerings: ["offering-1"],
    sendTiming: "custom_delay",
    delayValue: 1,
    delayUnit: "days",
  },
  "4": {
    automationName: "Engagement campaign",
    subject: "We miss you, {Customer_name}",
    body: `<p>Hi {Customer_name},</p><p>Just checking in. Here's a quick tip from {Coach_name}.</p>`,
    addCustomButton: false,
    buttonText: "",
    buttonLink: "",
    trigger: "abandoned_purchase",
    offerings: ["offering-1"],
    sendTiming: "custom_delay",
    delayValue: 30,
    delayUnit: "minutes",
  },
  "5": {
    automationName: "Post purchase followup",
    subject: "How was your experience, {Customer_name}?",
    body: DEFAULT_BODY,
    addCustomButton: true,
    buttonText: "Leave feedback",
    buttonLink: "https://example.com/feedback",
    trigger: "after_purchase",
    offerings: ["offering-1", "offering-2"],
    sendTiming: "custom_delay",
    delayValue: 3,
    delayUnit: "days",
  },
};

const DEFAULT_EDIT_VALUES: CreateAutomationFormValues = {
  automationName: "",
  subject: "Thank you for your purchase, {Customer_name}",
  body: DEFAULT_BODY,
  addCustomButton: false,
  buttonText: "",
  buttonLink: "",
  trigger: "after_purchase",
  offerings: [],
  sendTiming: "immediately",
  delayValue: 5,
  delayUnit: "minutes",
};

/** Returns form values to prefill when editing a row; falls back to defaults with name from record. */
export function getEditFormValues(
  record: EmailAutomationRow
): CreateAutomationFormValues {
  const stored = EDIT_FORM_VALUES_BY_KEY[record.key];
  if (stored) {
    return {...DEFAULT_EDIT_VALUES, ...stored, automationName: record.name};
  }
  return {...DEFAULT_EDIT_VALUES, automationName: record.name};
}

export type EmailAutomationTriggerFilter = "after_purchase" | "abandoned_purchase";

/** Automations the coach has created for a given trigger (placeholder: derived from mock edit data until API exists). */ //TODO!: CLEAR THIS
export function filterAutomationsByTrigger(
  rows: EmailAutomationRow[],
  trigger: EmailAutomationTriggerFilter
): EmailAutomationRow[] {
  return rows.filter((row) =>
    trigger === "after_purchase"
      ? row.trigger === "after-purchase"
      : row.trigger === "abandoned-purchase"
  );
}

/** Duplicate: same config as original except name = "[Original name] (Copy)" and offerings = []. Created as draft. */
export function getDuplicateFormValues(
  record: EmailAutomationRow
): CreateAutomationFormValues {
  const original = getEditFormValues(record);
  return {
    ...original,
    automationName: `${record.name} (Copy)`,
    offerings: [],
  };
}

const API_TO_UI_SHORT_CODE_MAP: Record<string, string> = {
  "{{customerName}}": "{Customer_name}",
  "{{coachName}}": "{Coach_name}",
  "{{coachEmail}}": "{Coach_email}",
  "{{offeringName}}": "{Offering_name}",
};

function reverseShortCodes(text: string): string {
  let result = text;
  for (const [apiCode, uiCode] of Object.entries(API_TO_UI_SHORT_CODE_MAP)) {
    result = result.split(apiCode).join(uiCode);
  }
  return result;
}

/** Quill uses `p { margin: 0 }`, so spacer blocks `<p><br></p>` visually collapse unless we normalize and style them. */
function normalizeParagraphSpacersForRichEditor(html: string): string {
  return html.replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, "<p><br></p>");
}

function convertHoursToDelayFields(hours: number): {
  delayValue: number;
  delayUnit: "minutes" | "hours" | "days";
} {
  if (hours % 24 === 0 && hours >= 24) {
    return {delayValue: hours / 24, delayUnit: "days"};
  }
  if (hours >= 1) {
    return {delayValue: hours, delayUnit: "hours"};
  }
  return {delayValue: Math.round(hours * 60), delayUnit: "minutes"};
}

export function getEditFormValuesFromApi(
  item: EmailAutomationApiItem,
  offeringOptions?: OfferingSelectOption[]
): CreateAutomationFormValues {
  const subject = reverseShortCodes(item.subject.template);
  const body = normalizeParagraphSpacersForRichEditor(
    reverseShortCodes(item.body.template)
  );
  const trigger =
    item.trigger === "after-purchase" ? "after_purchase" : "abandoned_purchase";
  const pivotOfferingIds = (item.settings?.pivot ?? []).map((p) => p.offeringId);
  const offerings = resolveOfferingIdsForMultiselect(
    pivotOfferingIds,
    offeringOptions ?? []
  );

  if (trigger === "abandoned_purchase") {
    return {
      automationName: item.automationName,
      subject,
      body,
      addCustomButton: item.customButton,
      buttonText: item.buttonText,
      buttonLink: item.buttonLink,
      trigger,
      offerings,
      sendTiming: "custom_delay",
      delayValue: 30,
      delayUnit: "minutes",
    };
  }

  const delayHoursRaw = Number(item.settings?.delayTime ?? 0);
  const sendTime = item.settings?.sendTime;
  /** Sparse offering-automation payloads often omit settings; delay 0 + non-custom means immediate send. */
  const sendTiming =
    sendTime === "immediate" || (sendTime !== "custom" && delayHoursRaw === 0)
      ? "immediately"
      : "custom_delay";
  const {delayValue, delayUnit} = convertHoursToDelayFields(
    Number.isFinite(delayHoursRaw) ? delayHoursRaw : 0
  );

  return {
    automationName: item.automationName,
    subject,
    body,
    addCustomButton: item.customButton,
    buttonText: item.buttonText,
    buttonLink: item.buttonLink,
    trigger,
    offerings,
    sendTiming,
    delayValue,
    delayUnit,
  };
}
